import { useState } from "preact/hooks";
import { useResource } from "@tenergy/web-core/dashboard";
import { adminReads } from "../api/admin-api.js";
import type { AuditRow } from "../api/types.js";
import { utc } from "../format.js";
import {
  Address,
  Blank,
  ErrorNote,
  inputClass,
  Note,
  Panel,
  ScreenHeader,
  Spinner,
  Table,
  Td,
  Th,
} from "../ui.js";

/**
 * Who changed what, when, and why.
 *
 * Every mutation on the operator surface writes an `audit_log` row in its own request, with a
 * reason the API refuses to accept as blank. This screen is the read of that table. It is
 * READ-ONLY for everybody, editor included — the table is append-only and nothing in the API
 * can change or delete a row.
 *
 * `before` and `after` are shown as they were stored, without prettifying: an audit trail that
 * reformats what it recorded is an audit trail somebody has to take on trust.
 */
export function AuditLogScreen() {
  const [action, setAction] = useState("");
  const [targetId, setTargetId] = useState("");
  const [expanded, setExpanded] = useState<string | null>(null);

  const audit = useResource(
    (signal) => {
      void signal;
      return adminReads.audit({
        ...(action === "" ? {} : { action }),
        ...(targetId === "" ? {} : { targetId }),
        limit: 200,
      });
    },
    [action, targetId],
  );

  const rows = audit.data?.data ?? [];

  return (
    <>
      <ScreenHeader
        title="Audit log"
        lead="Append-only. Every admin mutation writes one row here in the same transaction, and a row can never be changed or removed."
        action={
          <div class="flex gap-2">
            <input
              class={`${inputClass} w-48`}
              aria-label="Action"
              placeholder="action, e.g. price_config.add_version"
              value={action}
              onInput={(event) => setAction((event.currentTarget as HTMLInputElement).value.trim())}
            />
            <input
              class={`${inputClass} w-44`}
              aria-label="Target id"
              placeholder="target id"
              value={targetId}
              onInput={(event) => setTargetId((event.currentTarget as HTMLInputElement).value.trim())}
            />
          </div>
        }
      />

      <Panel title={`Entries (${rows.length})`}>
        {audit.loading ? (
          <Spinner />
        ) : audit.error !== null ? (
          <ErrorNote error={audit.error} onRetry={() => audit.reload()} />
        ) : (
          <Table
            rows={rows.length}
            empty="Nothing has been changed through the admin surface yet, or nothing matches this filter."
            head={
              <tr>
                <Th>When</Th>
                <Th>Actor</Th>
                <Th>Action</Th>
                <Th>Target</Th>
                <Th>Reason</Th>
                <Th>From</Th>
                <Th>{""}</Th>
              </tr>
            }
          >
            {rows.map((row) => (
              <AuditEntry
                key={row.id}
                row={row}
                open={expanded === row.id}
                onToggle={() => setExpanded(expanded === row.id ? null : row.id)}
              />
            ))}
          </Table>
        )}
      </Panel>
    </>
  );
}

function AuditEntry({
  row,
  open,
  onToggle,
}: {
  row: AuditRow;
  open: boolean;
  onToggle(): void;
}) {
  const hasDiff = row.before !== null || row.after !== null;
  return (
    <>
      <tr>
        <Td mono>{utc(row.created_at)}</Td>
        <Td mono>
          {row.actor_address === null ? (
            <span title={row.actor_type}>{row.actor_id ?? row.actor_type}</span>
          ) : (
            <Address value={row.actor_address} />
          )}
        </Td>
        <Td>{row.action}</Td>
        <Td mono>
          {row.target_type === null ? <Blank /> : `${row.target_type} ${row.target_id ?? ""}`}
        </Td>
        <Td>{row.reason}</Td>
        <Td mono>{row.request_ip ?? <Blank />}</Td>
        <Td>
          {hasDiff ? (
            <button
              type="button"
              onClick={onToggle}
              aria-expanded={open}
              class="text-[11px] font-medium text-accent-text hover:underline"
            >
              {open ? "hide" : "what changed"}
            </button>
          ) : null}
        </Td>
      </tr>
      {open && hasDiff ? (
        <tr>
          <Td class="bg-surface-raised" >
            <span class="sr-only">Recorded change</span>
          </Td>
          <td class="border-b border-line bg-surface-raised px-3 py-2" colSpan={6}>
            <div class="grid gap-2 lg:grid-cols-2">
              <div>
                <div class="text-[10px] uppercase tracking-wide text-ink-muted">before</div>
                <pre class="mt-1 max-h-48 overflow-auto whitespace-pre-wrap break-all num text-[10px] text-ink-muted">
                  {row.before === null ? "—" : JSON.stringify(row.before, null, 2)}
                </pre>
              </div>
              <div>
                <div class="text-[10px] uppercase tracking-wide text-ink-muted">after</div>
                <pre class="mt-1 max-h-48 overflow-auto whitespace-pre-wrap break-all num text-[10px] text-ink">
                  {row.after === null ? "—" : JSON.stringify(row.after, null, 2)}
                </pre>
              </div>
            </div>
          </td>
        </tr>
      ) : null}
    </>
  );
}
